import ksuid from 'ksuid';
import type { Db } from 'pg-boss';
import { WorkflowRunInProgressError } from '../error';
import type { WorkflowRun } from './types';

export function generateKSUID(prefix?: string): string {
  return `${prefix ? `${prefix}_` : ''}${ksuid.randomSync().string}`;
}

type WorkflowRunRow = {
  id: string;
  created_at: string | Date;
  updated_at: string | Date;
  resource_id: string | null;
  workflow_id: string;
  status: 'pending' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  input: string | unknown;
  output: string | unknown | null;
  error: string | null;
  current_step_id: string;
  timeline: string | Record<string, unknown>;
  paused_at: string | Date | null;
  resumed_at: string | Date | null;
  completed_at: string | Date | null;
  timeout_at: string | Date | null;
  retry_count: number;
  max_retries: number;
  priority: number;
  job_id: string | null;
  idempotency_key: string | null;
  parent_run_id: string | null;
  parent_step_id: string | null;
  parent_resource_id: string | null;
  scheduled_at: string | Date | null;
  singleton: boolean;
};

function isUniqueViolation(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  if ('code' in error && error.code === '23505') {
    return true;
  }
  const cause = 'cause' in error ? error.cause : undefined;
  return Boolean(cause && typeof cause === 'object' && 'code' in cause && cause.code === '23505');
}

function mapRowToWorkflowRun(row: WorkflowRunRow): WorkflowRun {
  return {
    id: row.id,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    resourceId: row.resource_id,
    workflowId: row.workflow_id,
    status: row.status,
    input: typeof row.input === 'string' ? JSON.parse(row.input) : row.input,
    output:
      typeof row.output === 'string'
        ? row.output.trim().startsWith('{') || row.output.trim().startsWith('[')
          ? JSON.parse(row.output)
          : row.output
        : (row.output ?? null),
    error: row.error,
    currentStepId: row.current_step_id,
    timeline: typeof row.timeline === 'string' ? JSON.parse(row.timeline) : row.timeline,
    pausedAt: row.paused_at ? new Date(row.paused_at) : null,
    resumedAt: row.resumed_at ? new Date(row.resumed_at) : null,
    completedAt: row.completed_at ? new Date(row.completed_at) : null,
    timeoutAt: row.timeout_at ? new Date(row.timeout_at) : null,
    retryCount: row.retry_count,
    maxRetries: row.max_retries,
    priority: row.priority,
    jobId: row.job_id,
    idempotencyKey: row.idempotency_key,
    parentRunId: row.parent_run_id,
    parentStepId: row.parent_step_id,
    parentResourceId: row.parent_resource_id,
    scheduledAt: row.scheduled_at ? new Date(row.scheduled_at) : null,
    singleton: Boolean(row.singleton),
  };
}

export async function insertWorkflowRun(
  {
    resourceId,
    workflowId,
    currentStepId,
    status,
    input,
    maxRetries,
    priority,
    timeoutAt,
    idempotencyKey,
    parentRunId,
    parentStepId,
    parentResourceId,
    scheduledAt,
    singleton = false,
  }: {
    resourceId?: string;
    workflowId: string;
    currentStepId: string;
    status: string;
    input: unknown;
    maxRetries: number;
    priority: number;
    timeoutAt: Date | null;
    idempotencyKey?: string;
    parentRunId?: string;
    parentStepId?: string;
    parentResourceId?: string;
    scheduledAt?: Date;
    singleton?: boolean;
  },
  db: Db,
): Promise<{ run: WorkflowRun; created: boolean }> {
  const runId = generateKSUID('run');
  const now = new Date();

  if (singleton) {
    const active = await getActiveSingletonRun(workflowId, db);
    if (active) {
      throw new WorkflowRunInProgressError(workflowId, active.id);
    }
  }

  let result: { rows: WorkflowRunRow[] };
  try {
    result = (await db.executeSql(
      `INSERT INTO workflow_runs (
      id,
      resource_id,
      workflow_id,
      current_step_id,
      status,
      input,
      max_retries,
      priority,
      timeout_at,
      created_at,
      updated_at,
      timeline,
      retry_count,
      idempotency_key,
      parent_run_id,
      parent_step_id,
      parent_resource_id,
      scheduled_at,
      singleton
    )
    VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19)
    ON CONFLICT (idempotency_key) WHERE idempotency_key IS NOT NULL DO NOTHING
    RETURNING *`,
      [
        runId,
        resourceId ?? null,
        workflowId,
        currentStepId,
        status,
        JSON.stringify(input),
        maxRetries,
        priority,
        timeoutAt,
        now,
        now,
        '{}',
        0,
        idempotencyKey ?? null,
        parentRunId ?? null,
        parentStepId ?? null,
        parentResourceId ?? null,
        scheduledAt ?? null,
        singleton,
      ],
    )) as { rows: WorkflowRunRow[] };
  } catch (error) {
    if (singleton && isUniqueViolation(error)) {
      const active = await getActiveSingletonRun(workflowId, db);
      throw new WorkflowRunInProgressError(workflowId, active?.id);
    }
    throw error;
  }

  if (result.rows[0]) {
    return { run: mapRowToWorkflowRun(result.rows[0]), created: true };
  }

  // Conflict - fetch the existing row
  const existing = await db.executeSql('SELECT * FROM workflow_runs WHERE idempotency_key = $1', [
    idempotencyKey,
  ]);

  if (!existing.rows[0]) {
    throw new Error(`Idempotency conflict: existing run not found for key "${idempotencyKey}"`);
  }

  return { run: mapRowToWorkflowRun(existing.rows[0]), created: false };
}

export async function getActiveSingletonRun(
  workflowId: string,
  db: Db,
): Promise<WorkflowRun | null> {
  const result = await db.executeSql(
    `SELECT * FROM workflow_runs
     WHERE workflow_id = $1
       AND singleton
       AND status IN ('pending', 'running')
     LIMIT 1`,
    [workflowId],
  );

  return result.rows[0] ? mapRowToWorkflowRun(result.rows[0] as WorkflowRunRow) : null;
}

export async function assertSingletonSlotAvailable(
  { workflowId, exceptRunId }: { workflowId: string; exceptRunId?: string },
  db: Db,
): Promise<void> {
  const active = await getActiveSingletonRun(workflowId, db);
  if (active && active.id !== exceptRunId) {
    throw new WorkflowRunInProgressError(workflowId, active.id);
  }
}

export async function getWorkflowRun(
  {
    runId,
    resourceId,
  }: {
    runId: string;
    resourceId?: string;
  },
  { exclusiveLock = false, db }: { exclusiveLock?: boolean; db: Db },
): Promise<WorkflowRun | null> {
  const lockSuffix = exclusiveLock ? 'FOR UPDATE' : '';

  const result = resourceId
    ? await db.executeSql(
        `SELECT * FROM workflow_runs 
        WHERE id = $1 AND resource_id = $2
        ${lockSuffix}`,
        [runId, resourceId],
      )
    : await db.executeSql(
        `SELECT * FROM workflow_runs 
        WHERE id = $1
        ${lockSuffix}`,
        [runId],
      );

  const run = result.rows[0];

  if (!run) {
    return null;
  }

  return mapRowToWorkflowRun(run);
}

export async function getWorkflowLastRun(
  {
    workflowId,
    resourceId,
  }: {
    workflowId: string;
    resourceId?: string;
  },
  db: Db,
): Promise<WorkflowRun | null> {
  const result = resourceId
    ? await db.executeSql(
        `SELECT * FROM workflow_runs
         WHERE workflow_id = $1 AND resource_id = $2
         ORDER BY created_at DESC
         LIMIT 1`,
        [workflowId, resourceId],
      )
    : await db.executeSql(
        `SELECT * FROM workflow_runs
         WHERE workflow_id = $1
         ORDER BY created_at DESC
         LIMIT 1`,
        [workflowId],
      );

  const run = result.rows[0];
  if (!run) {
    return null;
  }
  return mapRowToWorkflowRun(run);
}

export async function updateWorkflowRun(
  {
    runId,
    resourceId,
    data,
    expectedStatuses,
  }: {
    runId: string;
    resourceId?: string;
    data: Partial<WorkflowRun>;
    expectedStatuses?: string[];
  },
  db: Db,
): Promise<WorkflowRun | null> {
  const now = new Date();

  const updates: string[] = ['updated_at = $1'];
  const values: (string | number | Date | null | string[])[] = [now];
  let paramIndex = 2;

  if (data.status !== undefined) {
    updates.push(`status = $${paramIndex}`);
    values.push(data.status);
    paramIndex++;
  }
  if (data.currentStepId !== undefined) {
    updates.push(`current_step_id = $${paramIndex}`);
    values.push(data.currentStepId);
    paramIndex++;
  }
  if (data.timeline !== undefined) {
    updates.push(`timeline = $${paramIndex}`);
    values.push(JSON.stringify(data.timeline));
    paramIndex++;
  }
  if (data.pausedAt !== undefined) {
    updates.push(`paused_at = $${paramIndex}`);
    values.push(data.pausedAt);
    paramIndex++;
  }
  if (data.resumedAt !== undefined) {
    updates.push(`resumed_at = $${paramIndex}`);
    values.push(data.resumedAt);
    paramIndex++;
  }
  if (data.completedAt !== undefined) {
    updates.push(`completed_at = $${paramIndex}`);
    values.push(data.completedAt);
    paramIndex++;
  }
  if (data.output !== undefined) {
    updates.push(`output = $${paramIndex}`);
    values.push(JSON.stringify(data.output));
    paramIndex++;
  }
  if (data.error !== undefined) {
    updates.push(`error = $${paramIndex}`);
    values.push(data.error);
    paramIndex++;
  }
  if (data.retryCount !== undefined) {
    updates.push(`retry_count = $${paramIndex}`);
    values.push(data.retryCount);
    paramIndex++;
  }
  if (data.jobId !== undefined) {
    updates.push(`job_id = $${paramIndex}`);
    values.push(data.jobId);
    paramIndex++;
  }

  values.push(runId);
  const idParam = paramIndex;
  paramIndex++;

  if (resourceId) {
    values.push(resourceId);
    paramIndex++;
  }

  if (expectedStatuses && expectedStatuses.length > 0) {
    values.push(expectedStatuses);
    paramIndex++;
  }

  let whereClause = resourceId
    ? `WHERE id = $${idParam} AND resource_id = $${idParam + 1}`
    : `WHERE id = $${idParam}`;

  if (expectedStatuses && expectedStatuses.length > 0) {
    whereClause += ` AND status = ANY($${paramIndex - 1})`;
  }

  const query = `
    UPDATE workflow_runs 
    SET ${updates.join(', ')}
    ${whereClause}
    RETURNING *
  `;

  let result: { rows: WorkflowRunRow[] };
  try {
    result = (await db.executeSql(query, values)) as { rows: WorkflowRunRow[] };
  } catch (error) {
    if (data.status === 'running' && isUniqueViolation(error)) {
      throw new WorkflowRunInProgressError('unknown', runId);
    }
    throw error;
  }
  const run = result.rows[0];

  if (!run) {
    return null;
  }

  return mapRowToWorkflowRun(run);
}

export async function getWorkflowRuns(
  {
    resourceId,
    startingAfter,
    endingBefore,
    limit = 20,
    statuses,
    workflowId,
  }: {
    resourceId?: string;
    startingAfter?: string | null;
    endingBefore?: string | null;
    limit?: number;
    statuses?: string[];
    workflowId?: string;
  },
  db: Db,
): Promise<{
  items: WorkflowRun[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasMore: boolean;
  hasPrev: boolean;
}> {
  const conditions: string[] = [];
  const values: (string | number | string[] | Date)[] = [];
  let paramIndex = 1;

  if (resourceId) {
    conditions.push(`resource_id = $${paramIndex}`);
    values.push(resourceId);
    paramIndex++;
  }

  if (statuses && statuses.length > 0) {
    conditions.push(`status = ANY($${paramIndex})`);
    values.push(statuses);
    paramIndex++;
  }

  if (workflowId) {
    conditions.push(`workflow_id = $${paramIndex}`);
    values.push(workflowId);
    paramIndex++;
  }

  const cursorIds = [startingAfter, endingBefore].filter(Boolean) as string[];
  if (cursorIds.length > 0) {
    const cursorResult = await db.executeSql(
      'SELECT id, created_at FROM workflow_runs WHERE id = ANY($1)',
      [cursorIds],
    );
    const cursorMap = new Map<string, Date>();
    for (const row of cursorResult.rows) {
      cursorMap.set(
        row.id,
        typeof row.created_at === 'string' ? new Date(row.created_at) : row.created_at,
      );
    }

    if (startingAfter) {
      const cursor = cursorMap.get(startingAfter);
      if (cursor) {
        conditions.push(`created_at < $${paramIndex}`);
        values.push(cursor);
        paramIndex++;
      }
    }

    if (endingBefore) {
      const cursor = cursorMap.get(endingBefore);
      if (cursor) {
        conditions.push(`created_at > $${paramIndex}`);
        values.push(cursor);
        paramIndex++;
      }
    }
  }

  const whereClause = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';
  const actualLimit = Math.min(Math.max(limit, 1), 100) + 1;

  const isBackward = !!endingBefore && !startingAfter;

  const query = `
    SELECT * FROM workflow_runs
    ${whereClause}
    ORDER BY created_at ${isBackward ? 'ASC' : 'DESC'}
    LIMIT $${paramIndex}
  `;
  values.push(actualLimit);

  const result = await db.executeSql(query, values);
  const rows = result.rows;

  const hasExtraRow = rows.length > (limit ?? 20);
  const rawItems = hasExtraRow ? rows.slice(0, limit) : rows;

  if (isBackward) {
    rawItems.reverse();
  }

  const items = rawItems.map((row) => mapRowToWorkflowRun(row));

  const hasMore = isBackward ? items.length > 0 : hasExtraRow;
  const hasPrev = isBackward ? hasExtraRow : !!startingAfter && items.length > 0;

  const nextCursor = hasMore && items.length > 0 ? (items[items.length - 1]?.id ?? null) : null;
  const prevCursor = hasPrev && items.length > 0 ? (items[0]?.id ?? null) : null;

  return { items, nextCursor, prevCursor, hasMore, hasPrev };
}

/**
 * Run a callback inside a PostgreSQL transaction using a dedicated client.
 *
 * When a `pool` is provided, a dedicated client is checked out so that
 * BEGIN / COMMIT / ROLLBACK all execute on the **same** connection.
 * This is critical for `SELECT … FOR UPDATE` locks and any work that
 * yields to the event-loop inside the transaction (e.g. async step handlers).
 *
 * Falls back to the pg-boss `Db` adapter when no pool is given (unit-test path).
 */
export async function withPostgresTransaction<T>(
  db: Db,
  callback: (db: Db) => Promise<T>,
  pool?: {
    connect: () => Promise<{
      query: (text: string, values?: unknown[]) => Promise<unknown>;
      release: () => void;
    }>;
  },
): Promise<T> {
  let txDb: Db;
  let release: (() => void) | undefined;

  if (pool) {
    const client = await pool.connect();
    txDb = {
      executeSql: (text: string, values?: unknown[]) =>
        client.query(text, values) as Promise<{ rows: unknown[] }>,
    };
    release = () => client.release();
  } else {
    txDb = db;
  }

  try {
    await txDb.executeSql('BEGIN', []);
    const result = await callback(txDb);
    await txDb.executeSql('COMMIT', []);
    return result;
  } catch (error) {
    await txDb.executeSql('ROLLBACK', []);
    throw error;
  } finally {
    release?.();
  }
}
